import { useState, useEffect, useCallback, useRef } from "react";
import { adminInsert, adminUpdate, adminDelete } from "@/lib/admin-api";
import { supabase } from "@/lib/supabase";

/* ================================================================
   엑셀 스타일 낙찰물건 편집기
   - 셀 클릭 → 인라인 편집
   - Tab / Enter 키보드 네비게이션
   - 행 추가 / 삭제 / 일괄 저장
   ================================================================ */

interface AwardedItemRow {
  id: string | null;
  case_number: string;
  court: string;
  location: string;
  property_type: string;
  appraised_value: number;
  minimum_price: number;
  auction_date: string;
  failure_count: number;
  exclusive_area: number;
  building_age: number;
  actual_winning_bid: number;
  bidder_count: number;
  bid_competition_rate: string;
  expert_grade: string;
  expert_summary: string;
  expert_full_analysis: string;
  region: string;
  image_url: string;
  _isNew?: boolean;
  _isDeleted?: boolean;
}

const emptyRow = (): AwardedItemRow => ({
  id: null,
  case_number: "",
  court: "",
  location: "",
  property_type: "아파트",
  appraised_value: 0,
  minimum_price: 0,
  auction_date: "",
  failure_count: 0,
  exclusive_area: 0,
  building_age: 0,
  actual_winning_bid: 0,
  bidder_count: 0,
  bid_competition_rate: "",
  expert_grade: "추천",
  expert_summary: "",
  expert_full_analysis: "",
  region: "",
  image_url: "",
  _isNew: true,
});

const PROPERTY_TYPES = ["아파트", "다세대", "오피스텔", "단독주택", "상가", "빌라", "토지"];
const EXPERT_GRADES = ["적극추천", "추천", "관망"];

interface ColumnDef {
  key: keyof AwardedItemRow;
  label: string;
  width: number;
  type: "text" | "number" | "date" | "select" | "boolean";
  options?: string[];
  required?: boolean;
  group?: string;
  format?: (val: unknown) => string;
}

const GROUPS = [
  { key: "basic", label: "기본 정보", color: "#C4956A" },
  { key: "price", label: "가격 정보", color: "#10B981" },
  { key: "expert", label: "전문가 분석", color: "#F59E0B" },
  { key: "property", label: "물건 상세", color: "#6366F1" },
];

const columns: ColumnDef[] = [
  // 기본 정보
  { key: "case_number",          label: "사건번호",     width: 135, type: "text",   required: true,  group: "basic" },
  { key: "court",                label: "법원",         width: 110, type: "text",   required: true,  group: "basic" },
  { key: "location",             label: "소재지",       width: 160, type: "text",   required: true,  group: "basic" },
  { key: "property_type",        label: "종류",         width: 82,  type: "select", required: true,  group: "basic", options: PROPERTY_TYPES },
  { key: "auction_date",         label: "입찰일",       width: 105, type: "date",   required: true,  group: "basic" },
  { key: "failure_count",        label: "유찰",         width: 48,  type: "number", group: "basic",  format: (v) => `${v}회` },
  { key: "region",               label: "지역",         width: 65,  type: "text",   group: "basic" },
  // 가격 정보
  { key: "appraised_value",      label: "감정가",       width: 125, type: "number", required: true,  group: "price", format: (v) => (v as number) > 0 ? `${(v as number / 10000).toFixed(0)}만` : "" },
  { key: "minimum_price",        label: "최저가",       width: 125, type: "number", required: true,  group: "price", format: (v) => (v as number) > 0 ? `${(v as number / 10000).toFixed(0)}만` : "" },
  { key: "actual_winning_bid",   label: "낙찰가",       width: 125, type: "number", required: true,  group: "price", format: (v) => (v as number) > 0 ? `${(v as number / 10000).toFixed(0)}만` : "-" },
  { key: "bidder_count",         label: "응찰자",       width: 60,  type: "number", group: "price",  format: (v) => (v as number) > 0 ? `${v}명` : "" },
  { key: "bid_competition_rate", label: "경쟁률",       width: 60,  type: "text",   group: "price" },
  // 전문가 분석
  { key: "expert_grade",         label: "등급",         width: 72,  type: "select", group: "expert", options: EXPERT_GRADES },
  { key: "expert_summary",       label: "전문가 요약",  width: 260, type: "text",   group: "expert" },
  // 물건 상세
  { key: "exclusive_area",       label: "전용(㎡)",     width: 78,  type: "number", group: "property", format: (v) => (v as number) > 0 ? `${v}㎡` : "" },
  { key: "building_age",         label: "연식",         width: 55,  type: "number", group: "property", format: (v) => (v as number) > 0 ? `${v}년` : "" },
  { key: "image_url",            label: "이미지URL",    width: 160, type: "text",   group: "property" },
];

const gradeStyle = (grade: string) => {
  if (grade === "적극추천") return "bg-emerald-100 text-emerald-700 font-bold";
  if (grade === "추천") return "bg-amber-100 text-amber-700 font-bold";
  return "bg-gray-100 text-gray-500";
};

export default function AwardedExcelEditor() {
  const [rows, setRows] = useState<AwardedItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(null);
  const [editValue, setEditValue] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [activeGroup, setActiveGroup] = useState<string>("basic");
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("kangstudy_awarded_items")
        .select("*")
        .order("auction_date", { ascending: false });
      if (error) throw error;
      const mapped: AwardedItemRow[] = (data || []).map((item: Record<string, unknown>) => ({
        id: item.id as string,
        case_number: (item.case_number as string) || "",
        court: (item.court as string) || "",
        location: (item.location as string) || "",
        property_type: (item.property_type as string) || "아파트",
        appraised_value: (item.appraised_value as number) || 0,
        minimum_price: (item.minimum_price as number) || 0,
        auction_date: (item.auction_date as string) || "",
        failure_count: (item.failure_count as number) || 0,
        exclusive_area: (item.exclusive_area as number) || 0,
        building_age: (item.building_age as number) || 0,
        actual_winning_bid: (item.actual_winning_bid as number) || 0,
        bidder_count: (item.bidder_count as number) || 0,
        bid_competition_rate: (item.bid_competition_rate as string) || "",
        expert_grade: (item.expert_grade as string) || "추천",
        expert_summary: (item.expert_summary as string) || "",
        expert_full_analysis: (item.expert_full_analysis as string) || "",
        region: (item.region as string) || "",
        image_url: (item.image_url as string) || "",
        _isNew: false,
      }));
      setRows(mapped);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "오류";
      setMessage({ type: "error", text: `데이터 로드 실패: ${msg}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // 현재 그룹 필터링된 컬럼
  const filteredCols = columns.filter((c) => c.group === activeGroup);

  const handleCellClick = (rowIdx: number, colIdx: number) => {
    if (rows[rowIdx]._isDeleted) return;
    const col = filteredCols[colIdx];
    setActiveCell({ row: rowIdx, col: colIdx });
    const rawValue = rows[rowIdx][col.key];
    setEditValue(rawValue !== null && rawValue !== undefined ? String(rawValue) : "");
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        if ("select" in inputRef.current) (inputRef.current as HTMLInputElement).select();
      }
    }, 10);
  };

  const commitEdit = (rowIdx: number, colIdx: number, raw: string) => {
    const col = filteredCols[colIdx];
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== rowIdx) return r;
        const updated = { ...r };
        if (col.type === "number") {
          const num = raw.replace(/[^0-9.]/g, "");
          (updated as Record<string, unknown>)[col.key] = num ? parseFloat(num) : 0;
        } else {
          (updated as Record<string, unknown>)[col.key] = raw;
        }
        return updated;
      })
    );
    setActiveCell(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, rowIdx: number, colIdx: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit(rowIdx, colIdx, editValue);
      if (rowIdx < rows.length - 1) {
        setActiveCell({ row: rowIdx + 1, col: colIdx });
        const nextVal = rows[rowIdx + 1][filteredCols[colIdx].key];
        setEditValue(nextVal !== null && nextVal !== undefined ? String(nextVal) : "");
        setTimeout(() => {
          if (inputRef.current) { inputRef.current.focus(); }
        }, 10);
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      commitEdit(rowIdx, colIdx, editValue);
      const nextCol = e.shiftKey ? colIdx - 1 : colIdx + 1;
      if (nextCol >= 0 && nextCol < filteredCols.length) {
        setActiveCell({ row: rowIdx, col: nextCol });
        const nextVal = rows[rowIdx][filteredCols[nextCol].key];
        setEditValue(nextVal !== null && nextVal !== undefined ? String(nextVal) : "");
        setTimeout(() => {
          if (inputRef.current) { inputRef.current.focus(); }
        }, 10);
      }
    } else if (e.key === "Escape") {
      setActiveCell(null);
    }
  };

  const handleBlur = (rowIdx: number, colIdx: number) => { commitEdit(rowIdx, colIdx, editValue); };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const deleteRow = (rowIdx: number) => {
    if (rows[rowIdx]._isNew) {
      setRows((prev) => prev.filter((_, i) => i !== rowIdx));
    } else {
      setRows((prev) => prev.map((r, i) => (i === rowIdx ? { ...r, _isDeleted: true } : r)));
    }
  };

  const undoDelete = (rowIdx: number) => {
    setRows((prev) => prev.map((r, i) => (i === rowIdx ? { ...r, _isDeleted: false } : r)));
  };

  const saveAll = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const toDelete = rows.filter((r) => r._isDeleted && r.id);
      const toInsert = rows.filter((r) => r._isNew && !r._isDeleted);
      const toUpdate = rows.filter((r) => r.id && !r._isDeleted && !r._isNew);

      const allRows = [...toInsert, ...toUpdate];
      const invalid = allRows.find((r) => !r.case_number || !r.court || !r.location || !r.property_type || !r.auction_date);
      if (invalid) {
        setMessage({ type: "error", text: "사건번호, 법원, 소재지, 종류, 입찰일은 필수 입력입니다." });
        setSaving(false);
        return;
      }

      // 쓰기는 RLS 로 service_role 전용이라 Edge Function 이 대행한다(admin-api.ts).
      if (toDelete.length > 0) {
        await adminDelete("kangstudy_awarded_items", toDelete.map((r) => r.id as string));
      }
      if (toInsert.length > 0) {
        await adminInsert("kangstudy_awarded_items", toInsert.map(rowToPayload));
      }
      for (const r of toUpdate) {
        await adminUpdate("kangstudy_awarded_items", r.id as string, rowToPayload(r));
      }

      const saved = toInsert.length + toUpdate.length;
      const deleted = toDelete.length;
      setMessage({ type: "success", text: `${saved}건 저장 완료${deleted > 0 ? ` / ${deleted}건 삭제` : ""}` });
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "오류";
      setMessage({ type: "error", text: `저장 실패: ${msg}` });
    } finally {
      setSaving(false);
    }
  };

  const rowToPayload = (r: AwardedItemRow) => ({
    case_number: r.case_number, court: r.court, location: r.location, property_type: r.property_type,
    appraised_value: r.appraised_value, minimum_price: r.minimum_price, auction_date: r.auction_date,
    failure_count: r.failure_count, exclusive_area: r.exclusive_area, building_age: r.building_age,
    actual_winning_bid: r.actual_winning_bid, bidder_count: r.bidder_count,
    bid_competition_rate: r.bid_competition_rate, expert_grade: r.expert_grade,
    expert_summary: r.expert_summary, expert_full_analysis: r.expert_full_analysis,
    region: r.region, image_url: r.image_url,
  });

  const changedCount = rows.filter((r) => r._isNew || r._isDeleted).length;
  const visibleRows = rows.filter((r) => !r._isDeleted);
  const deletedRows = rows.filter((r) => r._isDeleted && r.id);

  return (
    <div className="space-y-4">
      {/* 상단 바 */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-bold text-[#1C1917]">낙찰물건 관리</h3>
          <span className="text-xs text-[#8C8780]">총 {visibleRows.length}건</span>
          {changedCount > 0 && (
            <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
              변경 {changedCount}건 미저장
            </span>
          )}
          {deletedRows.length > 0 && (
            <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full">삭제예정 {deletedRows.length}건</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={addRow} className="px-3 py-1.5 text-xs font-medium border border-[#E8E5E0] text-[#5C5650] rounded-lg hover:bg-[#FAFAF8] transition-all whitespace-nowrap">
            <i className="ri-add-line mr-1"></i>행 추가
          </button>
          <button
            onClick={saveAll}
            disabled={saving || changedCount === 0}
            className="px-4 py-1.5 text-xs font-medium bg-[#1C1917] text-white rounded-lg hover:bg-[#C4956A] transition-all whitespace-nowrap disabled:opacity-40"
          >
            {saving ? <><i className="ri-loader-4-line animate-spin mr-1"></i>저장 중...</> : <><i className="ri-save-line mr-1"></i>전체 저장</>}
          </button>
        </div>
      </div>

      {/* 메시지 */}
      {message && (
        <div className={`px-4 py-2.5 rounded-lg text-xs font-medium ${message.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {message.type === "success" ? <i className="ri-check-line mr-1"></i> : <i className="ri-error-warning-line mr-1"></i>}
          {message.text}
        </div>
      )}

      {/* 그룹 탭 */}
      <div className="flex items-center gap-1 border-b border-[#E8E5E0] pb-0">
        {GROUPS.map((g) => (
          <button
            key={g.key}
            onClick={() => setActiveGroup(g.key)}
            className={`px-3 py-2 text-xs font-medium rounded-t-lg transition-all whitespace-nowrap border-b-2 -mb-px ${
              activeGroup === g.key
                ? "bg-white border-[#C4956A] text-[#C4956A]"
                : "border-transparent text-[#8C8780] hover:text-[#5C5650]"
            }`}
          >
            {g.label}
          </button>
        ))}
        <div className="ml-auto text-xs text-[#B8B3AE] pb-2">
          <i className="ri-keyboard-line mr-1"></i>Tab / Enter / Esc
        </div>
      </div>

      {/* 엑셀 그리드 */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#8C8780]">
          <i className="ri-loader-4-line animate-spin mr-2"></i>
          <span className="text-sm">불러오는 중...</span>
        </div>
      ) : (
        <div className="border border-[#D8D3CC] rounded-lg overflow-hidden bg-white">
          <div className="overflow-auto" style={{ maxHeight: "65vh" }}>
            <table className="border-collapse" style={{ minWidth: filteredCols.reduce((s, c) => s + c.width, 0) + 80 }}>
              <thead>
                <tr className="bg-[#F5F3F0] sticky top-0 z-10">
                  <th className="border-b border-r border-[#D8D3CC] px-2 py-2 text-xs font-semibold text-[#8C8780] text-center w-10 min-w-[40px]">#</th>
                  {filteredCols.map((col) => (
                    <th
                      key={col.key}
                      className="border-b border-r border-[#D8D3CC] px-2 py-2 text-xs font-semibold text-[#5C5650] text-left whitespace-nowrap"
                      style={{ width: col.width, minWidth: col.width }}
                    >
                      {col.label}
                      {col.required && <span className="text-red-400 ml-0.5">*</span>}
                    </th>
                  ))}
                  <th className="border-b border-[#D8D3CC] px-2 py-2 text-xs font-semibold text-[#8C8780] text-center w-10">삭제</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={filteredCols.length + 2} className="px-4 py-16 text-center text-sm text-[#B8B3AE]">
                      등록된 낙찰물건이 없습니다.{" "}
                      <button onClick={addRow} className="text-[#C4956A] hover:underline font-medium">첫 물건 추가하기</button>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row, rowIdx) => (
                    <tr
                      key={row.id || `new-${rowIdx}`}
                      className={`border-b border-[#E8E5E0] hover:bg-[#FAFAF8] transition-colors ${row._isNew ? "bg-blue-50/30" : ""}`}
                    >
                      {/* 행 번호 + 등급 뱃지 */}
                      <td className="border-r border-[#E8E5E0] px-1 py-1.5 text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-[10px] text-[#B8B3AE]">{rowIdx + 1}</span>
                          <span className={`text-[9px] px-1 py-0.5 rounded ${gradeStyle(row.expert_grade)}`}>
                            {row.expert_grade === "적극추천" ? "적극" : row.expert_grade === "추천" ? "추천" : "관망"}
                          </span>
                        </div>
                      </td>

                      {filteredCols.map((col, colIdx) => {
                        const isActive = activeCell?.row === rowIdx && activeCell?.col === colIdx;
                        const value = row[col.key];
                        const displayValue = col.format && !isActive
                          ? col.format(value)
                          : String(value !== null && value !== undefined ? value : "");

                        return (
                          <td
                            key={col.key}
                            className={`border-r border-[#E8E5E0] px-2 py-1.5 text-xs cursor-cell transition-colors ${
                              isActive ? "bg-white ring-2 ring-[#C4956A] ring-inset" : ""
                            }`}
                            style={{ width: col.width, minWidth: col.width }}
                            onClick={() => handleCellClick(rowIdx, colIdx)}
                          >
                            {isActive && col.type === "select" ? (
                              <select
                                ref={inputRef as React.Ref<HTMLSelectElement>}
                                value={editValue}
                                onChange={(e) => { setEditValue(e.target.value); commitEdit(rowIdx, colIdx, e.target.value); }}
                                onBlur={() => handleBlur(rowIdx, colIdx)}
                                onKeyDown={(e) => handleKeyDown(e, rowIdx, colIdx)}
                                className="w-full text-xs border-none outline-none bg-transparent p-0"
                                autoFocus
                              >
                                {(col.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            ) : isActive ? (
                              <input
                                ref={inputRef as React.Ref<HTMLInputElement>}
                                type={col.type === "date" ? "date" : "text"}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => handleBlur(rowIdx, colIdx)}
                                onKeyDown={(e) => handleKeyDown(e, rowIdx, colIdx)}
                                className="w-full text-xs border-none outline-none bg-transparent p-0"
                                autoFocus
                              />
                            ) : (
                              <span className={`block w-full truncate ${
                                col.type === "number" && !value ? "text-[#D8D3CC]" :
                                col.type === "number" ? "text-[#1C1917] text-right font-medium" :
                                col.required && !value ? "text-red-300 italic" : "text-[#5C5650]"
                              }`}>
                                {displayValue || (col.required ? "필수" : "")}
                              </span>
                            )}
                          </td>
                        );
                      })}

                      <td className="px-1 py-1.5 text-center">
                        <button
                          onClick={() => deleteRow(rowIdx)}
                          className="w-6 h-6 rounded flex items-center justify-center mx-auto text-[#B8B3AE] hover:text-red-500 hover:bg-red-50 transition-all"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 삭제 예정 행 복원 */}
      {deletedRows.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-xs font-medium text-red-700 mb-2"><i className="ri-error-warning-line mr-1"></i>삭제 예정 ({deletedRows.length}건) — 저장 버튼 전에 취소 가능</p>
          <div className="flex flex-wrap gap-2">
            {rows.map((r, i) => r._isDeleted && r.id ? (
              <div key={r.id} className="flex items-center gap-2 bg-white border border-red-200 rounded px-2 py-1">
                <span className="text-xs text-red-600 line-through">{r.case_number}</span>
                <button onClick={() => undoDelete(i)} className="text-xs text-[#C4956A] hover:underline font-medium">복원</button>
              </div>
            ) : null)}
          </div>
        </div>
      )}

      {/* 하단 통계 요약 */}
      {visibleRows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "전체 낙찰물건", value: `${visibleRows.length}건`, icon: "ri-auction-line", color: "#C4956A" },
            { label: "적극추천", value: `${visibleRows.filter((r) => r.expert_grade === "적극추천").length}건`, icon: "ri-star-line", color: "#10B981" },
            { label: "평균 응찰자", value: (() => {
              const withBidder = visibleRows.filter((r) => r.bidder_count > 0);
              if (!withBidder.length) return "-";
              const avg = Math.round(withBidder.reduce((s, r) => s + r.bidder_count, 0) / withBidder.length);
              return `${avg}명`;
            })(), icon: "ri-group-line", color: "#F59E0B" },
            {
              label: "평균 낙찰가",
              value: (() => {
                const withBid = visibleRows.filter((r) => r.actual_winning_bid > 0);
                if (!withBid.length) return "-";
                const avg = Math.round(withBid.reduce((s, r) => s + r.actual_winning_bid, 0) / withBid.length / 10000);
                return `${avg.toLocaleString()}만`;
              })(),
              icon: "ri-funds-line",
              color: "#6366F1",
            },
          ].map((stat, i) => (
            <div key={i} className="bg-[#FAFAF8] border border-[#E8E5E0] rounded-lg px-3 py-2.5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: stat.color + "15" }}>
                <i className={`${stat.icon} text-sm`} style={{ color: stat.color }}></i>
              </div>
              <div>
                <p className="text-[10px] text-[#8C8780]">{stat.label}</p>
                <p className="text-sm font-bold text-[#1C1917]">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}